import React from 'react';
import {
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Button,
  Collapse
} from '@material-ui/core';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon
} from '@material-ui/icons';
import styled from 'styled-components';
import TourPricingValidator from '../../services/TourPricingValidator';

const StyledPaper = styled(Paper)`
  margin: 16px 0;
  padding: 16px;
`;

const ValidationSection = styled.div`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const CorrectionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e0e0e0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const CorrectionLabel = styled.span`
  font-weight: 500;
  color: #333;
`;

const CorrectionValue = styled.span`
  font-weight: 600;
  color: #f57c00;
`;

const ExpandButton = styled(Button)`
  margin-top: 8px;
`;

const Alert = styled(Paper)`
  padding: 12px 16px;
  margin: 8px 0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  
  ${props => props.severity === 'success' && `
    background-color: #e8f5e8;
    color: #2e7d32;
    border: 1px solid #4caf50;
  `}
  
  ${props => props.severity === 'error' && `
    background-color: #ffebee;
    color: #c62828;
    border: 1px solid #f44336;
  `}
  
  ${props => props.severity === 'warning' && `
    background-color: #fff3e0;
    color: #ef6c00;
    border: 1px solid #ff9800;
  `}
  
  ${props => props.severity === 'info' && `
    background-color: #e3f2fd;
    color: #1565c0;
    border: 1px solid #2196f3;
  `}
  
  .MuiSvgIcon-root {
    margin-right: 8px;
  }
`;

/**
 * Componente para mostrar alertas de validación de precios
 */
const PricingValidationAlert = ({ tourData, pricing, onApplyCorrections }) => {
  const [expanded, setExpanded] = React.useState(false);
  
  // Generar reporte de validación
  const validationReport = React.useMemo(() => {
    if (!tourData || !pricing) return null;
    return TourPricingValidator.generateValidationReport(tourData, pricing);
  }, [tourData, pricing]);

  if (!validationReport) {
    return null;
  }

  const { summary, details, corrections, errors, warnings, correctedPricing } = validationReport;

  // Si todo está válido, mostrar mensaje de éxito
  if (summary.isValid) {
    return (
      <Alert severity="success">
        <CheckIcon />
        <Typography variant="body2">
          ✓ Todos los cálculos de precios son correctos
        </Typography>
      </Alert>
    );
  }

  // Formatear moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const handleApplyCorrections = () => {
    if (onApplyCorrections && correctedPricing) {
      onApplyCorrections(correctedPricing);
    }
  };

  return (
    <StyledPaper>
      {/* Resumen de errores */}
      <Alert 
        severity={summary.totalErrors > 0 ? "error" : "warning"}
      >
        {summary.totalErrors > 0 ? <ErrorIcon /> : <WarningIcon />}
        <div>
          <Typography variant="subtitle2" gutterBottom>
            {summary.totalErrors > 0 ? "Errores detectados en los cálculos:" : "Advertencias en los cálculos:"}
          </Typography>
          
          <Typography variant="body2">
            {summary.totalErrors} errores, {summary.totalWarnings} advertencias
          </Typography>
        </div>
      </Alert>

      {/* Detalles de validación */}
      <Collapse in={expanded}>
        <ValidationSection>
          <Typography variant="subtitle2" gutterBottom style={{ color: '#f57c00' }}>
            Detalles de Validación:
          </Typography>

          {/* Días efectivos */}
          <ValidationSection>
            <Typography variant="body2" gutterBottom>
              <strong>Días Efectivos:</strong>
            </Typography>
            <ListItem dense>
              <ListItemIcon>
                {details.days.isValid ? <CheckIcon color="primary" /> : <ErrorIcon color="error" />}
              </ListItemIcon>
              <ListItemText
                primary={`Esperado: ${details.days.expected} días, Actual: ${details.days.actual} días`}
                secondary={details.days.isValid ? "✓ Correcto" : "✗ Incorrecto"}
              />
            </ListItem>
          </ValidationSection>

          {/* GPS */}
          <ValidationSection>
            <Typography variant="body2" gutterBottom>
              <strong>Cálculo GPS:</strong>
            </Typography>
            <ListItem dense>
              <ListItemIcon>
                {details.gps.isValid ? <CheckIcon color="primary" /> : <ErrorIcon color="error" />}
              </ListItemIcon>
              <ListItemText
                primary={`Esperado: ${formatCurrency(details.gps.expected)}, Actual: ${formatCurrency(details.gps.actual)}`}
                secondary={`Tarifa diaria: ${formatCurrency(details.gps.dailyRate)}`}
              />
            </ListItem>
          </ValidationSection>

          {/* Total */}
          <ValidationSection>
            <Typography variant="body2" gutterBottom>
              <strong>Total del Tour:</strong>
            </Typography>
            <ListItem dense>
              <ListItemIcon>
                {details.total.isValid ? <CheckIcon color="primary" /> : <ErrorIcon color="error" />}
              </ListItemIcon>
              <ListItemText
                primary={`Esperado: ${formatCurrency(details.total.expected)}, Actual: ${formatCurrency(details.total.actual)}`}
                secondary={details.total.isValid ? "✓ Correcto" : "✗ Incorrecto"}
              />
            </ListItem>
          </ValidationSection>
        </ValidationSection>
      </Collapse>

      {/* Correcciones disponibles */}
      {Object.keys(corrections).length > 0 && (
        <ValidationSection>
          <Typography variant="subtitle2" gutterBottom style={{ color: '#f57c00' }}>
            Correcciones Disponibles:
          </Typography>
          
          {corrections.gps && (
            <CorrectionItem>
              <CorrectionLabel>GPS Device:</CorrectionLabel>
              <div>
                <CorrectionValue>
                  {formatCurrency(corrections.gps.from)} → {formatCurrency(corrections.gps.to)}
                </CorrectionValue>
                <Typography variant="caption" display="block">
                  ({formatCurrency(corrections.gps.dailyRate)}/día × {corrections.gps.days} días)
                </Typography>
              </div>
            </CorrectionItem>
          )}

          {corrections.vipInsurance && (
            <CorrectionItem>
              <CorrectionLabel>VIP Insurance:</CorrectionLabel>
              <div>
                <CorrectionValue>
                  {formatCurrency(corrections.vipInsurance.from)} → {formatCurrency(corrections.vipInsurance.to)}
                </CorrectionValue>
                <Typography variant="caption" display="block">
                  ({formatCurrency(corrections.vipInsurance.dailyRate)}/día × {corrections.vipInsurance.days} días)
                </Typography>
              </div>
            </CorrectionItem>
          )}

          {/* Botón para aplicar correcciones */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleApplyCorrections}
            style={{ marginTop: 16 }}
            fullWidth
          >
            Aplicar Correcciones
          </Button>
        </ValidationSection>
      )}

      {/* Botón para expandir/colapsar */}
      <ExpandButton
        size="small"
        onClick={() => setExpanded(!expanded)}
        endIcon={expanded ? <CollapseIcon /> : <ExpandIcon />}
      >
        {expanded ? "Ocultar detalles" : "Ver detalles"}
      </ExpandButton>
    </StyledPaper>
  );
};

export default PricingValidationAlert; 


// WEBPACK FOOTER //
// ./src/components/tour/PricingValidationAlert.js